import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import {
  groupByVarietyType,
  mergeVarietyRows,
  calculateBoxes,
  getShipmentWeightNested,
  DEFAULT_BOX_KG,
} from '../../../utils/groupByVarietyType';

/** Commodity -> type -> variety sections of one cargo row (card or stop). */
export const weightSections = (row) => getShipmentWeightNested(row).map((c) => ({
  commodity: c.commodity,
  totalWeight: Number(c.total_weight) || 0,
  groups: groupByVarietyType(c.types || []),
}));

export const boxes82 = (kg) => {
  const n = Number(kg);
  return n > 0 ? Math.round(n / DEFAULT_BOX_KG) : 0;
};

/** Expanded body of one stop: the commodity -> type -> variety accordion. */
const StopWeightSections = ({ sections, stopId, isDark, commodityIsOpen, toggleCommodity, pluralize = false }) => {
  const { t } = useTranslation();
  // Legacy text ("N boxes") unless the caller opts into plural-aware, locale-formatted counts.
  const boxesText = (n) => (pluralize
    ? t('seaTracking.card.boxesCount', { count: n })
    : `${n.toLocaleString()} ${t('seaTracking.card.boxes', 'boxes')}`);
  return (
      <div className="flex flex-col">
        {sections.map((section, sIdx) => (
          section.groups.length > 0 && (
            <div key={section.commodity || sIdx}>
              {section.commodity && (() => {
                const cKey = `stop:${stopId}:${section.commodity}`;
                const cOpen = commodityIsOpen(cKey);
                return (
                <button
                  type="button"
                  aria-expanded={cOpen}
                  onClick={() => toggleCommodity(cKey)}
                  className={`w-full flex items-center gap-x-2 min-w-0 px-2 py-1.5 text-xs font-bold text-left rounded-none ${
                    isDark ? 'bg-[#2a2a2a] text-white' : 'bg-[#f3f4f6] text-[#1f2937]'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5 min-w-0 flex-1">
                    <ChevronDown size={12} className={`shrink-0 transition-transform ${cOpen ? '' : '-rotate-90'}`} />
                    <span className="min-w-0 truncate">{section.commodity}</span>
                  </span>
                  {section.totalWeight > 0 && (
                      <span className="shrink-0 text-right font-normal whitespace-nowrap">
                        {boxesText(boxes82(section.totalWeight))}
                        <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                          | {Number(section.totalWeight).toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg
                        </span>
                      </span>
                  )}
                </button>
                );
              })()}
              {(!section.commodity || commodityIsOpen(`stop:${stopId}:${section.commodity}`)) && (
              <div className={`rounded border text-xs ${isDark ? 'border-[#333]' : 'border-[#e5e7eb]'} ${
                section.commodity ? 'rounded-t-none border-t-0' : ''
              }`}>
                {section.groups.map((group, gIdx, groups) => {
                  const varieties = mergeVarietyRows(group.varieties);
                  const showTypeRow = varieties.length > 1;
                  return (
                  <div key={`${group.type}-${gIdx}`}>
                    {showTypeRow && (() => {
                      const typeBoxTotal = varieties.reduce((sum, v) => {
                        const b = calculateBoxes(v);
                        return sum + (b.equivBoxes82 || boxes82(v.weight_kg));
                      }, 0);
                      return (
                        <div
                          className={`flex justify-between gap-x-1.5 px-2 py-1.5 font-semibold ${
                            isDark ? 'bg-[#282a29]' : 'bg-[#f3f4f6]'
                          } ${gIdx !== 0 ? (isDark ? 'border-t border-[#333]' : 'border-t border-[#e5e7eb]') : ''}`}
                        >
                          <span>{group.type}</span>
                          <span>
                            {typeBoxTotal > 0 ? (
                              <>
                                <span className="font-bold">{boxesText(typeBoxTotal)} ({DEFAULT_BOX_KG} kg)</span>
                                <span className={`ml-1 font-normal text-[0.625rem] ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                                  | {group.subtotal.toLocaleString()} kg
                                </span>
                              </>
                            ) : (
                              <>{group.subtotal.toLocaleString()} kg</>
                            )}
                          </span>
                        </div>
                      );
                    })()}
                    {varieties.map((item, idx) => {
                      const boxInfo = calculateBoxes(item);
                      return (
                        <div
                          key={`${group.type}-${item.variety}-${idx}`}
                          className={`flex justify-between pr-2 py-1 ${
                            showTypeRow ? 'pl-5' : 'pl-2'
                          } ${
                            idx !== varieties.length - 1 || gIdx !== groups.length - 1
                              ? isDark ? 'border-b border-[#333]' : 'border-b border-[#e5e7eb]'
                              : ''
                          } ${!showTypeRow && gIdx !== 0 ? (isDark ? 'border-t border-[#333]' : 'border-t border-[#e5e7eb]') : ''}`}
                        >
                          <span className={`min-w-0 break-words pr-2 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                            {item.variety || '—'}
                          </span>
                          <span className="shrink-0 text-right">
                            {(boxInfo.equivBoxes82 || boxes82(item.weight_kg)) ? (
                              <>
                                <span className="font-bold">{(boxInfo.equivBoxes82 || boxes82(item.weight_kg)).toLocaleString()} × {DEFAULT_BOX_KG} kg</span>
                                <span className={`block font-normal text-[0.625rem] ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                                  {(Number(item.weight_kg) || 0).toLocaleString()} kg
                                </span>
                              </>
                            ) : (
                              <span className="font-medium">{(Number(item.weight_kg) || 0).toLocaleString()} kg</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
                })}
              </div>
              )}
            </div>
          )
        ))}
      </div>
  );
};

export default StopWeightSections;
